import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { useCookies } from 'react-cookie';
import { 
    Flex, HStack, Button, ButtonGroup, Text, Icon, Center, Select,
    Table, Thead, Tbody, Tfoot, Tr, Th, Td, TableCaption, TableContainer
} from '@chakra-ui/react';
import ReactPaginate from 'react-paginate';
import { BsArrowLeftCircle, BsArrowRightCircle } from 'react-icons/bs';
import { withNamespaces } from 'react-i18next';

import { FormattedDate, FormattedTime, FormattedNumber } from 'react-intl';
import "../../../styles/index.scss";

import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.module.css';

import { apiLoadListUser } from '../../../redux/apis/UserApis';
import { apiLoadListUserLog } from '../../../redux/apis/UserLogApis';
import { getUserLogList, getUserList, getUserLogTotalCount, getUserLogOffset } from '../../../redux/selectors';
import NoDataPage from '../../../components/NoData';

function UserLogs({ userLogList, totalCount, offset, userList, apiLoadListUserLog, apiLoadListUser, t }) {
    const [ cookies ] = useCookies(["loginCookie"]);
    const [ fromDate, setFromDate ] = useState(new Date());
    const [ toDate, setToDate ] = useState(new Date());
    const [ searchParams, setSearchParams ] = useState({ });
    const [ isSearchingDate, setIsSearchingDate ] = useState(false);
    const mounted = useRef();

    useEffect(() => {
        if(!mounted.current) {
            apiLoadListUserLog({ 
                company_id: cookies?.loginCookie?.data?.company_id,
            });
            apiLoadListUser({ company_id: cookies?.loginCookie?.data?.company_id });
            mounted.current = true;
        }
    });

    useEffect(() => {
        setIsSearchingDate(false);
    }, [userLogList]);
    
    return (
        <React.Fragment>
            <Flex flexDirection={{ base: "column", md: "row", lg: "row" }} borderBottomWidth="2px" 
                px={4} py={2} gap={2} align="center" justify="space-between"
            >
                <Flex flexDirection={{ base: "column", md: "row", lg: "row" }} gap={2} align="center" justify="flex-start">
                    <Text>{t("From_Date")}</Text>
                    <DatePicker dateFormat="dd/MM/yyyy" 
                        selected={fromDate}
                        onChange={
                            (date) => setFromDate(date)
                        }
                        showIcon
                        placeholderText="Từ ngày"
                        className='date-picker'
                    />

                    <Text>{t("To_Date")}</Text>
                    <DatePicker dateFormat="dd/MM/yyyy" 
                        selected={toDate}
                        onChange={
                            (date) => setToDate(date)
                        }
                        showIcon
                        placeholderText="Đến ngày"
                        className='date-picker'
                    />

                    <Button colorScheme='purple'
                        isLoading={isSearchingDate}
                        loadingText={t("Searching")}
                        onClick={() => {
                            setIsSearchingDate(true);
                            setSearchParams({ ...searchParams, fromDate, toDate });
                            setTimeout(() => {
                                apiLoadListUserLog({ 
                                    company_id: cookies?.loginCookie?.data?.company_id,
                                    ...searchParams,
                                    fromDate,
                                    toDate
                                });
                            }, 500);
                        }
                    }>
                        {t("Search_By_Date")}
                    </Button>
                </Flex>

                <Select placeholder={t("Select_Account")} maxW="250px"
                    onChange={(evt) => {
                        if(fromDate && toDate) {
                            apiLoadListUserLog({ 
                                company_id: cookies?.loginCookie?.data?.company_id,
                                fromDate,
                                toDate,
                                user_id: evt.currentTarget.value
                            });
                        } else {
                            apiLoadListUserLog({ 
                                company_id: cookies?.loginCookie?.data?.company_id,
                                user_id: evt.currentTarget.value
                            });
                        }
                    }}
                >
                    {
                        userList.map(item => (
                            <React.Fragment key={item.id}>
                                <option value={item.id}>{item.full_name}</option>
                            </React.Fragment>
                        ))
                    }
                </Select>
            </Flex>

            <PaginatedItems items={userLogList} totalCount={totalCount} itemOffset={offset} searchParams={searchParams} t={t}
                apiLoadListUserLog={apiLoadListUserLog}
            />
            {
                !(userLogList && userLogList.length > 0) && <NoDataPage text="Hiện tại không có dữ liệu"/>
            }
        </React.Fragment>
    )
}

function PaginatedItems({ items, totalCount, itemOffset, searchParams, apiLoadListUserLog, t }) {
    const [ cookies ] = useCookies(["loginCookie"]);

    const handlePageClick = (event) => {
        const newOffset = (event.selected * itemsPerPage);
        
        apiLoadListUserLog({ 
            company_id: cookies?.loginCookie?.data?.company_id, 
            fromDate: searchParams.fromDate, 
            toDate: searchParams.toDate,
            limit: itemsPerPage, 
            offset: newOffset 
        });
    } 

    const itemsPerPage = 10;
    const pageCount =  Math.ceil(totalCount / itemsPerPage);

    return (
        <>
            <TableContainer width="100%" px="20px" py="5px">
                <Table variant="simple" size="sm">
                    <Thead>
                        <Tr bg="#EAF0F0">
                            <Th>{t("Index")}</Th>
                            <Th>{t("Company")}</Th>
                            <Th>{t("Account")}</Th>
                            <Th>{t("Method")}</Th>
                            <Th>{t("Url")}</Th>
                            <Th>{t("Data")}</Th>
                            <Th>{t("Created_At")}</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {
                            items && items.map((item, idx) => {
                                const bodyDatas = item.body_datas;
                                delete bodyDatas["company_id"];
                                delete bodyDatas["user_id"];
                                delete bodyDatas["created_at"];
                                delete bodyDatas["created_by"];
                                delete bodyDatas["updated_at"];
                                delete bodyDatas["updated_by"];
                                delete bodyDatas["password"];
                                delete bodyDatas["confirm_password"];
                                
                                return (
                                    <Tr key={item.id}>
                                        <Td>
                                            <Text fontWeight="600">{itemOffset + idx + 1}</Text>
                                        </Td>
                                        <Td>
                                            <Text fontWeight="600">{item.companyDetail?.name}</Text>
                                        </Td>
                                        <Td>
                                            <Text fontWeight="600">{item.userDetail?.full_name}</Text>
                                        </Td>
                                        <Td>
                                            <Text fontWeight="600">{item.method}</Text>
                                        </Td>
                                        <Td>
                                            <Text fontWeight="600">{item.original_url}</Text>
                                        </Td>
                                        <Td>
                                            <Text maxW="50vw" fontWeight="600" whiteSpace="initial">{JSON.stringify(bodyDatas)}</Text>
                                        </Td>
                                        <Td>
                                            <Text fontWeight="600">
                                                <FormattedDate value={item.created_at} /> <FormattedTime value={item.created_at} />
                                            </Text>
                                        </Td>
                                    </Tr>
                                )
                            })
                        }
                    </Tbody>
                </Table>
            </TableContainer>

            {/* <Flex w="100%" justify="center" my="10px" position={{base: "relative", md: "fixed", lg: "fixed"}} bottom="0px"> */}
                <ReactPaginate 
                    breakLabel="..."
                    onPageChange={handlePageClick}
                    pageRangeDisplayed={2}
                    pageCount={pageCount}
                    previousLabel={
                        <Center>
                            <Icon boxSize={8} as={BsArrowLeftCircle} />
                        </Center>
                    }
                    nextLabel={
                        <Center>
                            <Icon boxSize={8} as={BsArrowRightCircle} />
                        </Center>
                    }
                    renderOnZeroPageCount={null}
                    containerClassName='pagination'
                    pageClassName='page-item'
                    activeClassName='active'
                    previousClassName='previous-item'
                    nextClassName='next-item'
                />
            {/* </Flex> */}
        </>
    )
}

const mapStateToProps = (state) => {
    const userLogList           =   getUserLogList(state);
    const totalCount            =   getUserLogTotalCount(state);
    const offset                =   getUserLogOffset(state);
    const userList              =   getUserList(state);
    return { userLogList, totalCount, offset, userList };
}

const mapDispatchToProps = (dispatch) => ({
    apiLoadListUserLog:         payload => dispatch(apiLoadListUserLog(payload)),
    apiLoadListUser:            payload => dispatch(apiLoadListUser(payload)),
})

export default connect(mapStateToProps, mapDispatchToProps)(withNamespaces()(UserLogs));